// ─── Helper centralizado de cálculos financieros del presupuesto ─────────────
// Garantiza que TODOS los componentes (FinancialsTab, ResumenFinancieroSection,
// KPIs, donut, barras, total final) usen la MISMA lógica.
// Replica el modelo Excel CEN:
//   - Si precioInputMode === "con_iva", divide los precios entrantes por 1.10
//   - GA se asigna 100% al M.O. (no se reparte)
//   - Los % por grupo sobreescriben los globales (override parcial: solo el campo definido)

/**
 * Numeración jerárquica determinista de la planilla de cómputo.
 * Devuelve un array paralelo a `items` con el N° EFECTIVO de cada fila.
 *
 * Reglas:
 *  - Secciones (is_group, nivel 0): "N.0". Preserva el N° manual si existe; sino correlativo.
 *  - Sub/sub-sub-secciones (is_group, nivel ≥1): preserva el N° manual; si está vacío lo
 *    sintetiza como prefijo_del_padre + correlativo (solo para mostrar / placeholder).
 *  - Ítems normales y "Materiales menores" (is_group=false): SIEMPRE correlativos dentro de
 *    su grupo más cercano → prefijo_del_grupo + posición. Esto corrige el bug de "1.1" en
 *    ítems con numero="" (ej. Materiales menores) y mantiene la secuencia al agregar/borrar/mover.
 *
 * NO altera ningún total: el N° es puramente cosmético / de referencia.
 * @param {Array} items
 * @returns {string[]}
 */
export function computeItemNumbers(items = []) {
  const display = new Array(items.length).fill("");

  const stripTrailingZeros = (num) => {
    const parts = String(num || "").split(".").map((s) => s.trim()).filter(Boolean);
    while (parts.length > 1 && parts[parts.length - 1] === "0") parts.pop();
    return parts;
  };

  let sectionCount = 0;
  const stack = []; // { level, prefix: string[] }
  const childCounter = new Map(); // prefixKey → nº de hijos ya emitidos

  for (let i = 0; i < items.length; i++) {
    const it = items[i] || {};
    if (it.is_group) {
      const lvl = Number(it.nivel_jerarquia || 0);
      // Cerrar los grupos abiertos de nivel >= al actual.
      while (stack.length && stack[stack.length - 1].level >= lvl) stack.pop();
      const stored = stripTrailingZeros(it.numero);
      let prefix;
      if (lvl === 0) {
        sectionCount += 1;
        const major = stored.length ? stored[0] : String(sectionCount);
        prefix = [major];
        display[i] = `${major}.0`;
      } else {
        const parentPrefix = stack.length ? stack[stack.length - 1].prefix : [String(sectionCount || 1)];
        if (stored.length) {
          prefix = stored; // preservar N° manual de la sub-sección
        } else {
          const pkey = parentPrefix.join(".");
          const n = (childCounter.get(pkey) || 0) + 1;
          childCounter.set(pkey, n);
          prefix = [...parentPrefix, String(n)];
        }
        display[i] = prefix.join(".");
      }
      stack.push({ level: lvl, prefix });
      childCounter.set(prefix.join("."), 0); // reset del contador de hijos de este grupo
    } else {
      const groupPrefix = stack.length ? stack[stack.length - 1].prefix : [];
      const pkey = groupPrefix.join(".");
      const n = (childCounter.get(pkey) || 0) + 1;
      childCounter.set(pkey, n);
      display[i] = pkey ? `${pkey}.${n}` : String(n);
    }
  }
  return display;
}

const _round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * ⚠️ NO duplicar cálculos: ÚNICA FUENTE de cálculo por ítem en el frontend — replica EXACTO
 * `calc_item` (backend/routers/budgets_utils.py). BLOQUE 7 (Feb 2026) — bug reiterativo
 * "Total M.O. fantasma": una copia local en ItemRow.jsx cruzaba los campos `total_mat`/
 * `total_mo` (costo c/IVA) con los de VENTA (`total_mat_final`/`total_mo_final`, que
 * incluyen G.A./G.M.O.). Cualquier componente que necesite las columnas por ítem del
 * editor DEBE consumir este helper — está PROHIBIDO reimplementar el cálculo inline.
 * Si se corrige una fórmula, corregirla ACÁ y en `calc_item` (backend) al mismo tiempo.
 *
 * @param {Object} item - ítem del presupuesto (cantidad, costo_unit_mat, costo_unit_mo, ...)
 * @param {Object} globalPct - { ganancia_mat_pct, ga_pct, gmo_pct } del presupuesto
 * @param {Object|null} groupPct - overrides del grupo/sub-grupo (mismo shape), o null
 * @param {String} precioInputMode - "con_iva" | "sin_iva"
 * @param {Number} groupMatTotal - Σ mat_sin_iva del grupo (para ítems "menores_pct")
 * @returns {Object} mismos campos/nombres que `calc_item`: mat_sin_iva, total_mat,
 *   mo_sin_iva, total_mo, costo_total, tot_gm, tot_ga, tot_gmo, pu_mat, pu_mo,
 *   total_mat_final, total_mo_final, precio_unit, iva_item, total_final.
 */
export function computeItemLive(item, globalPct = {}, groupPct = null, precioInputMode = "sin_iva", groupMatTotal = 0) {
  const is_menores = item.tipo_calculo === "menores_pct";
  const cant = parseFloat(item.cantidad || 0) || (is_menores ? 1 : 0);
  let cuMatInput = parseFloat(item.costo_unit_mat || 0);
  const cuMoInput = parseFloat(item.costo_unit_mo || 0);

  // Ítem "materiales menores de montaje" → cu_mat = Σ(mat_sin_iva del grupo) × %manual / cant
  if (is_menores) {
    const pctM = parseFloat(item.menores_pct || 0) / 100;
    const baseSum = Number(groupMatTotal || 0); // ya viene en "sin_iva"
    let cuTarget = (baseSum * pctM) / (cant || 1);
    if (precioInputMode === "con_iva") cuTarget = cuTarget * 1.10;
    cuMatInput = cuTarget;
  }

  // Modelo Excel CEN: si entrada con IVA, dividir por 1.10. Redondeo 2 dec (consistencia
  // editor/PDF/Excel — mismo punto que el backend).
  const cu_mat = precioInputMode === "con_iva" ? _round2(cuMatInput / 1.10) : _round2(cuMatInput);
  const cu_mo = precioInputMode === "con_iva" ? _round2(cuMoInput / 1.10) : _round2(cuMoInput);

  const pick = (field, def) => {
    if (groupPct && groupPct[field] != null && groupPct[field] !== "") return Number(groupPct[field]);
    const v = globalPct[field];
    if (v != null && v !== "") return Number(v);
    return Number(def);
  };
  const gmPct = pick("ganancia_mat_pct", 10) / 100;
  const gaPct = pick("ga_pct", 10) / 100;
  const gmoPct = pick("gmo_pct", 45) / 100;

  const mat_sin_iva = _round2(cant * cu_mat);
  const total_mat = _round2(mat_sin_iva * 1.10);
  const mo_sin_iva = _round2(cant * cu_mo);
  const total_mo = _round2(mo_sin_iva * 1.10);
  const costo_total = _round2(mat_sin_iva + mo_sin_iva);

  const tot_gm = _round2(mat_sin_iva * gmPct);
  const tot_ga = _round2(costo_total * gaPct);
  const tot_gmo = _round2(mo_sin_iva * gmoPct);

  // Modelo Excel CEN: la G.A. se asigna 100% al M.O. en el PRECIO (no se reparte).
  const total_mat_final = _round2(mat_sin_iva + tot_gm);
  const total_mo_final = _round2(mo_sin_iva + tot_gmo + tot_ga);

  const pu_mat = cant > 0 ? _round2(total_mat_final / cant) : 0;
  const pu_mo = cant > 0 ? _round2(total_mo_final / cant) : 0;
  const precio_unit = _round2(pu_mat + pu_mo);

  const sub_venta = _round2(total_mat_final + total_mo_final);
  const iva_item = _round2(sub_venta * 0.10);
  const total_final = _round2(sub_venta + iva_item);

  return {
    mat_sin_iva, total_mat, mo_sin_iva, total_mo, costo_total,
    tot_gm, tot_ga, tot_gmo,
    pu_mat, pu_mo,
    total_mat_final, total_mo_final,
    precio_unit, iva_item, total_final,
  };
}

/**
 * Calcula métricas agregadas del presupuesto en tiempo real.
 * @param {Array} items - lista de items (incluyendo filas de grupo con is_group=true).
 * @param {Object} globalPct - { ganancia_mat_pct, ga_pct, gmo_pct }
 * @param {String} precioInputMode - "con_iva" | "sin_iva" (default "sin_iva")
 * @param {Object|null} descuento - { activo, porcentaje, aplica_sobre } (optional)
 * @returns {Object} métricas completas
 */
export function computeBudgetLive(items, globalPct = {}, precioInputMode = "sin_iva", descuento = null) {
  let mat_directo = 0;
  let mo_directo = 0;
  let gm = 0;
  let ga = 0;
  let gmo = 0;
  let subtotal_neto_acc = 0;
  const bySection = [];
  let currentGroup = null;
  let currentGroupPct = null;

  // PDF REV1 E.1 — Pre-pass: Σ mat_sin_iva por grupo (excluyendo ítems "menores_pct").
  const groupSums = new Map(); // groupIdx → suma mat_sin_iva
  {
    let gIdx = -1;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.is_group) {
        // FIX jerarquía v2 — cada grupo Y sub-grupo es límite de cálculo del Σ
        // para "menores_pct" (% sobre los materiales de su sub-sección).
        gIdx = i; groupSums.set(gIdx, 0);
        continue;
      }
      if (it.tipo_calculo === "menores_pct") continue;
      const cant = parseFloat(it.cantidad || 0);
      let cu = parseFloat(it.costo_unit_mat || 0);
      // FIX redondeo: misma política que backend — redondear a 2 dec
      if (precioInputMode === "con_iva") cu = _round2(cu / 1.10);
      else cu = _round2(cu);
      if (gIdx >= 0) groupSums.set(gIdx, (groupSums.get(gIdx) || 0) + cant * cu);
    }
  }

  // BLOQUE 11 Parte E — computeBudgetLive ya NO mantiene una 3ra copia del cálculo por
  // ítem: delega SIEMPRE en `computeItemLive` (misma fuente que la grilla del editor) y
  // acumula los campos YA REDONDEADOS por ítem — igual que `calculate_budget` (backend),
  // que suma total_mat_final+total_mo_final (ambos redondeados a 2 dec por calc_item)
  // y redondea el total de nuevo. Antes acumulaba productos SIN redondear por ítem →
  // los KPIs/donut podían divergir centavos del total guardado.
  let calcGroupIdx = -1;
  for (let idx = 0; idx < items.length; idx++) {
    const it = items[idx];
    if (it.is_group) {
      // Frontera de cálculo para menores: cualquier grupo/sub-grupo.
      calcGroupIdx = idx;
      // FIX jerarquía — los sub-grupos (nivel>=1) NO cambian el contexto de
      // porcentajes ni crean una nueva sección: heredan el grupo principal.
      if (Number(it.nivel_jerarquia || 0) !== 0) {
        continue;
      }
      // Capturar overrides del grupo: cualquier campo no-null/no-"" activa el override parcial.
      const gp = {
        ganancia_mat_pct: it.ganancia_mat_pct,
        ga_pct: it.ga_pct,
        gmo_pct: it.gmo_pct,
      };
      const hasAny = Object.values(gp).some((v) => v != null && v !== "");
      currentGroupPct = hasAny ? gp : null;
      currentGroup = {
        numero: it.numero || "",
        nombre: it.descripcion || "Sin nombre",
        mat: 0,
        mo: 0,
        gm: 0,
        ga: 0,
        gmo: 0,
      };
      bySection.push(currentGroup);
      continue;
    }

    const groupMatTotal = groupSums.get(calcGroupIdx) || 0;
    const calc = computeItemLive(it, globalPct, currentGroupPct, precioInputMode, groupMatTotal);

    mat_directo += calc.mat_sin_iva;
    mo_directo += calc.mo_sin_iva;
    gm += calc.tot_gm;
    ga += calc.tot_ga;
    gmo += calc.tot_gmo;
    subtotal_neto_acc += calc.total_mat_final + calc.total_mo_final;

    if (currentGroup) {
      currentGroup.mat += calc.mat_sin_iva;
      currentGroup.mo += calc.mo_sin_iva;
      currentGroup.gm += calc.tot_gm;
      currentGroup.ga += calc.tot_ga;
      currentGroup.gmo += calc.tot_gmo;
    }
  }

  const costo_directo = mat_directo + mo_directo;
  const utilidades = gm + gmo;
  const total_ganancias = gm + ga + gmo;
  // SUBTOTAL NETO = Σ (total_mat_final + total_mo_final) por ítem, ya redondeados — SSOT
  // idéntica al backend (calculate_budget), NO Σ de componentes sin redondear.
  const subtotal_neto = _round2(subtotal_neto_acc);

  // ─── Descuento (Feb 2026) ───────────────────────────────────────────────
  let descuento_activo = false;
  let descuento_porcentaje = 0;
  let descuento_aplica_sobre = "utilidades";
  let descuento_base = 0;
  let descuento_monto = 0;
  let subtotal_con_descuento = subtotal_neto;

  if (descuento && descuento.activo) {
    descuento_activo = true;
    descuento_porcentaje = parseFloat(descuento.porcentaje || 0);
    descuento_aplica_sobre = descuento.aplica_sobre || "utilidades";

    if (descuento_aplica_sobre === "mano_de_obra") {
      descuento_base = mo_directo;
    } else {
      descuento_base = total_ganancias;
    }
    descuento_monto = descuento_base * (descuento_porcentaje / 100);
    subtotal_con_descuento = subtotal_neto - descuento_monto;
  }

  const iva = subtotal_con_descuento * 0.10;
  const total = subtotal_con_descuento + iva;
  const margen = costo_directo > 0 ? (utilidades / costo_directo) * 100 : 0;
  // Fix HE (2026-10-20): "Ganancia Neta = (G.M + G.MO) - G.A" · "Margen Neto = Ganancia Neta / Subtotal Neto (sin IVA)"
  // · "ROI = Ganancia Neta / (Costo Directo + G.A.)" (CEN). `margen` queda como estaba (Utilidades / Costo Directo).
  const ganancia_neta = utilidades - ga;
  const margen_neto = subtotal_neto > 0 ? (ganancia_neta / subtotal_neto) * 100 : 0;
  const roi = (costo_directo + ga) > 0 ? (ganancia_neta / (costo_directo + ga)) * 100 : 0;

  return {
    mat_directo,
    mo_directo,
    gm,
    ga,
    gmo,
    costo_directo,
    utilidades,
    total_ganancias,
    subtotal_neto,
    // Descuento
    descuento_activo,
    descuento_porcentaje: Number(descuento_porcentaje.toFixed(3)),   // Fix HW: se muestra con hasta 3 decimales; el cálculo usó el valor completo
    descuento_aplica_sobre,
    descuento_base,
    descuento_monto,
    subtotal_con_descuento,
    // Totals (adjusted if discount active)
    iva,
    total,
    margen,
    ganancia_neta,
    margen_neto,
    roi,
    bySection,
  };
}
